import React, { useState, useEffect } from "react";
import {
    ArrowLeft,
    Save,
    User,
    Loader2,
    UserCircle,
} from "lucide-react";
import { useMeeting } from "../../../../context/MeetingContext";
import { MeetingSession, SpeakerMapping as SpeakerMappingType } from "../../../../types";

interface SpeakerMappingProps {
    session: MeetingSession;
    onBack: () => void;
    onComplete: () => void;
}

const SpeakerMapping: React.FC<SpeakerMappingProps> = ({ session, onBack, onComplete }) => {
    const { updateSpeakerMapping, getTranscription } = useMeeting();
    const [mappings, setMappings] = useState<SpeakerMappingType[]>([]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    // Initialize mappings from session or detect from transcription
    useEffect(() => {
        const initMappings = async () => {
            setLoading(true);

            if (session.speaker_mapping && session.speaker_mapping.length > 0) {
                // Use existing mappings
                setMappings(session.speaker_mapping);
            } else {
                // Detect speakers from transcription
                try {
                    const transcription = await getTranscription(session.id);
                    if (transcription?.content && transcription.content.length > 0) {
                        // Get unique speaker labels from transcription
                        const uniqueSpeakers = [...new Set(transcription.content.map(s => s.speaker_label))];
                        const detectedMappings: SpeakerMappingType[] = uniqueSpeakers.map(label => ({
                            speaker_label: label,
                            participant_name: '',
                            participant_role: '',
                        }));
                        setMappings(detectedMappings);
                    } else {
                        // Fallback to default 2 speakers
                        setMappings([
                            { speaker_label: 'spk_0', participant_name: '', participant_role: '' },
                            { speaker_label: 'spk_1', participant_name: '', participant_role: '' },
                        ]);
                    }
                } catch (err) {
                    console.error('Failed to get transcription for speaker detection:', err);
                    // Fallback
                    setMappings([
                        { speaker_label: 'spk_0', participant_name: '', participant_role: '' },
                        { speaker_label: 'spk_1', participant_name: '', participant_role: '' },
                    ]);
                }
            }

            setLoading(false);
        };

        initMappings();
    }, [session.id]);

    const handleNameChange = (speakerLabel: string, name: string) => {
        setMappings(prev => prev.map(m =>
            m.speaker_label === speakerLabel
                ? { ...m, participant_name: name }
                : m
        ));
    };

    const handleRoleChange = (speakerLabel: string, role: string) => {
        setMappings(prev => prev.map(m =>
            m.speaker_label === speakerLabel
                ? { ...m, participant_role: role }
                : m
        ));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await updateSpeakerMapping(session.id, mappings);
            onComplete();
        } catch (err) {
            console.error('Failed to save speaker mapping:', err);
        } finally {
            setSaving(false);
        }
    };

    const getSpeakerDisplayNumber = (label: string) => {
        const match = label.match(/spk_(\d+)/);
        return match ? parseInt(match[1]) + 1 : label;
    };

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={onBack}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    disabled={saving}
                >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <div>
                    <h2 className="text-lg font-semibold">Identify Speakers</h2>
                    <p className="text-sm text-gray-500">{session.title || 'Untitled Recording'}</p>
                </div>
            </div>

            {/* Description */}
            <p className="text-sm text-gray-600 mb-6">
                Assign names and roles to each speaker detected in the recording.
                This helps improve the readability of the transcription.
            </p>

            {/* Speaker mappings */}
            <div className="flex-1 overflow-y-auto space-y-6">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-violet-500 animate-spin mb-4" />
                        <p className="text-gray-500">Detecting speakers...</p>
                    </div>
                ) : mappings.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <p>No speakers detected in this transcription.</p>
                    </div>
                ) : (
                    mappings.map((mapping, index) => (
                        <div
                            key={mapping.speaker_label}
                            className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                                    <UserCircle className="w-6 h-6 text-violet-600" />
                                </div>
                                <div>
                                    <span className="font-medium text-gray-900">
                                        Speaker {getSpeakerDisplayNumber(mapping.speaker_label)}
                                    </span>
                                    <span className="text-sm text-gray-500 ml-2">
                                        ({mapping.speaker_label})
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Name input */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Name
                                    </label>
                                    <input
                                        type="text"
                                        value={mapping.participant_name}
                                        onChange={(e) => handleNameChange(mapping.speaker_label, e.target.value)}
                                        placeholder="Enter speaker name"
                                        className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                    />
                                </div>

                                {/* Role input */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Role
                                    </label>
                                    <input
                                        type="text"
                                        value={mapping.participant_role}
                                        onChange={(e) => handleRoleChange(mapping.speaker_label, e.target.value)}
                                        placeholder="Enter role (e.g., Doctor, Manager)"
                                        className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Save button */}
            <div className="flex justify-end pt-4 border-t border-gray-200 mt-4">
                <button
                    onClick={handleSave}
                    disabled={saving || loading}
                    className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <Save className="w-4 h-4" />
                            Save Mapping
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default SpeakerMapping;
